import moment from 'moment';
import { get } from 'lodash-es';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';
import { createKeyedMemo } from 'utils/keyed-memo';
import type { TypeCurveScenarioOption } from 'types/type-curve-scenario-options';
import type {
  AreaConfigResponse,
  AreaSelection,
  DevelopmentPaceScenarioFormData,
  DevelopmentPaceScenarioResponse,
  NavItem,
  PaceModel,
  WellLifecycleParams,
} from './types';
import type {
  AreaConfigDraft,
  AreaGeometrySelection,
  EphemeralScenarioDefinition,
} from 'types/dps-capture';
import type { LtdTimingParams, PrePermitTimingParams } from 'types/dps-timing';
import {
  LTD_STYLE_LAG_FIELDS,
  LTD_STYLE_TIMING_FIELDS,
  PRE_PERMIT_TIMING_SECTIONS,
  TIMING_SECTIONS,
  pickPrePermitTiming,
  prePermitTimingFromLtd,
  timingBackendKey,
  timingKey,
} from './timing-sections';

export type FormMode = 'create' | 'edit';

export const GLOBAL_SELECTION_ID = 'global';

// ─────────────────────────────────────
// Constants
// ─────────────────────────────────────

export const DEFAULT_GEOMETRY_PRIORITY = [
  'Slot',
  'Unit',
  'Geology',
  'AOI',
  'TITLE',
  'StructureGrid',
  'StructureGridCollection',
];

export const PACE_MODEL_OPTIONS: { label: string; value: PaceModel }[] = [
  { label: 'Years to Full Development', value: 'years_to_full_development' },
  { label: 'Fixed Rate', value: 'fixed_rate' },
];

export const SLOT_PRIORITIZATION_OPTIONS = [
  {
    label: 'Legacy Ranking · v1 default',
    value: 'legacy_ranking',
    helperText: 'Ranks by tc_status_rank → tc_mboe_20_1 → depth → slot_number',
  },
];

export const REFERENCE_DATE_MODE_LABELS = {
  current_month: 'Current Month',
  fixed: 'Fixed',
} as const;

/**
 * Returns a label for the pace parameter input based on the pace model.
 * @param {PaceModel} paceModel - The selected pace model
 * @return {string} The label string for the pace parameter input
 */
export function getPaceParamLabel(paceModel: PaceModel): string {
  return paceModel === 'years_to_full_development'
    ? 'Years to Full Development'
    : 'Slots per Unit per Year';
}

// ─────────────────────────────────────
// Defaults
// ─────────────────────────────────────

// Pinned starting values for the very first area of a brand-new scenario, from
// the mock. These are product-visible defaults, not zeros: a fresh area opens
// ready to schedule rather than needing every lag filled in.
const DEFAULT_LTD_TIMING = {
  ltdStartOffsetMonths: 0,
  ltdPermitToSpudLagMonths: 6,
  ltdSpudToCompletionLagMonths: 3,
  ltdCompletionToFirstProductionLagMonths: 2,
};

// Each Create New Well status starts from LTD's defaults, the DB column defaults too
// (migration 20261015213605).
export const DEFAULT_WELL_LIFECYCLE: WellLifecycleParams = {
  ...DEFAULT_LTD_TIMING,
  ...prePermitTimingFromLtd(DEFAULT_LTD_TIMING),
  permitReferenceToSpudLagMonths: 4,
  permitSpudToCompletionLagMonths: 3,
  permitCompletionToFirstProductionLagMonths: 2,
  ducReferenceToCompletionLagMonths: 3,
  ducCompletionToFirstProductionLagMonths: 2,
};

export const DEFAULT_PACE_MODEL_VALUE = 5;

export const DEFAULT_AREA_SELECTION: Omit<AreaSelection, 'id'> = {
  status: 'new',
  geometryType: 'area',
  configurableId: null,
  category: '',
  areaName: '',
  unitType: '',
  unitName: '',
  slotId: '',
  slotLabel: '',
  alias: '',
  notes: '',
  paceModelValue: DEFAULT_PACE_MODEL_VALUE,
  includeActiveUpside: false,
  wellLifecycle: { ...DEFAULT_WELL_LIFECYCLE },
};

// Client-side mirrors of the server validations (spec 5.2). Lags are integer
// months in [0, 120]; the LTD start offset may lead or lag the reference date,
// so it spans [-120, 120]; the per-area pace value under years-to-full-development
// is [1, 50], and under fixed rate any positive number.
//
// One deliberate exception to "mirrors": YTFD_MIN is 1 while the server allows
// anything above 0 (area_config.rb has `greater_than: 0` plus a <= 50 check).
// That floor is product, not drift - INS-11291 AC 6 specifies "per-area
// years-to-full-development 1..50". Integrality is NOT part of that spec: AC 6
// binds integers to the lag inputs only, which is why validateRange enforces it
// and validatePaceValue must not (ONCALL-840).
export const LAG_MIN = 0;
export const LAG_MAX = 120;
export const LTD_OFFSET_MIN = -120;
export const LTD_OFFSET_MAX = 120;
export const YTFD_MIN = 1;
export const YTFD_MAX = 50;

export const DEFAULT_FORM_VALUES: DevelopmentPaceScenarioFormData = {
  global: {
    configurationName: '',
    notes: '',
    paceModel: 'years_to_full_development',
    referenceDateMode: 'current_month',
    referenceDate: moment().startOf('month').format('YYYY-MM-DD'),
    slotPrioritizationMethod: 'legacy_ranking',
    geometryPriority: [...DEFAULT_GEOMETRY_PRIORITY],
    typeCurveScenarioId: null,
    typeCurveScenarioName: null,
  },
  areas: [],
};

// ─────────────────────────────────────
// Helpers
// ─────────────────────────────────────

/**
 * Returns the display name for an area based on alias, area name, or unit name.
 * @param {AreaSelection} area - The area selection to get display name for
 * @return {string} The display name string
 */
/** The display name an area falls back to before it is named. */
export const UNNAMED_AREA_LABEL = 'New Area';

export function getAreaDisplayName(area: AreaSelection): string {
  return area.alias || area.areaName || area.unitName || UNNAMED_AREA_LABEL;
}

/**
 * Formats a month offset for the process flow connector label.
 * @param {number | undefined} value - The month offset value
 * @return {string} Formatted label
 */
export function formatMonths(value: number | undefined): string {
  if (value === undefined || value === null) return '\u2014';
  // A gap can run backward (an LTD-style Permit before its Reference Date), so it reads −6.
  return `${value < 0 ? '\u2212' : '+'}${Math.abs(value)} months`;
}

/**
 * Computes a date label by adding months to a reference date.
 * @param {moment.Moment} referenceDate - The starting date
 * @param {number[]} offsets - Array of month offsets to add cumulatively
 * @return {string} Formatted date string (e.g., "January 2027")
 */
export function computeDateLabel(
  referenceDate: moment.Moment,
  offsets: number[]
): string {
  const total = offsets.reduce((sum, v) => sum + (v || 0), 0);
  return referenceDate.clone().add(total, 'months').format('MMMM YYYY');
}

/**
 * The preview dates of one LTD-style section (LTD and each Create New Well status), as
 * DevelopmentPaceScenario::SlotScheduler#compute_ltd_dates dates the first slot: the start
 * offset lands on Spud, Permit is the permit-to-spud lag BEFORE it, and Completion and First
 * Production follow Spud.
 * @param {moment.Moment} referenceDate - The scenario's reference date
 * @param {object} lags - The section's start offset and three lags, in months
 * @param {number} lags.offset - Reference Date → Spud
 * @param {number} lags.permitToSpud - Permit → Spud
 * @param {number} lags.spudToCompletion - Spud → Completion
 * @param {number} lags.completionToFirstProduction - Completion → First Production
 * @return {object} Each milestone's label (e.g. "January 2027")
 */
export function ltdStyleDateLabels(
  referenceDate: moment.Moment,
  {
    offset,
    permitToSpud,
    spudToCompletion,
    completionToFirstProduction,
  }: {
    offset: number;
    permitToSpud: number;
    spudToCompletion: number;
    completionToFirstProduction: number;
  }
): {
  permit: string;
  spud: string;
  completion: string;
  firstProduction: string;
} {
  return {
    permit: computeDateLabel(referenceDate, [offset, -(permitToSpud || 0)]),
    spud: computeDateLabel(referenceDate, [offset]),
    completion: computeDateLabel(referenceDate, [offset, spudToCompletion]),
    firstProduction: computeDateLabel(referenceDate, [
      offset,
      spudToCompletion,
      completionToFirstProduction,
    ]),
  };
}

/**
 * The connector labels between the milestones ltdStyleDateLabels dates, so each label is the
 * gap between the two dates beside it. Reference Date → Permit is the start offset less the
 * permit-to-spud lag (the offset lands on Spud), and runs backward when the lag is longer.
 * @param {object} lags - The section's start offset and three lags, in months
 * @param {number} lags.offset - Reference Date → Spud
 * @param {number} lags.permitToSpud - Permit → Spud
 * @param {number} lags.spudToCompletion - Spud → Completion
 * @param {number} lags.completionToFirstProduction - Completion → First Production
 * @return {object} Each connector's label (e.g. "+6 months", "−6 months")
 */
export function ltdStyleConnectorLabels({
  offset,
  permitToSpud,
  spudToCompletion,
  completionToFirstProduction,
}: {
  offset: number;
  permitToSpud: number;
  spudToCompletion: number;
  completionToFirstProduction: number;
}): {
  referenceToPermit: string;
  permitToSpud: string;
  spudToCompletion: string;
  completionToFirstProduction: string;
} {
  return {
    referenceToPermit: formatMonths(
      offset === undefined || offset === null
        ? offset
        : offset - (permitToSpud || 0)
    ),
    permitToSpud: formatMonths(permitToSpud),
    spudToCompletion: formatMonths(spudToCompletion),
    completionToFirstProduction: formatMonths(completionToFirstProduction),
  };
}

// ─────────────────────────────────────
// Field-level validators (used by form.Field in T5/T6)
// ─────────────────────────────────────

/**
 * Validates that a string value is non-empty.
 * @param {string} value - The string to validate
 * @return {string | undefined} Error message or undefined if valid
 */
export function validateRequired(value: string): string | undefined {
  return value.trim() ? undefined : 'This field is required';
}

/**
 * Checks whether an area selection has all required fields filled.
 * @param {AreaSelection} area - The area to validate
 * @return {boolean} True if valid
 */
export function isAreaSelectionComplete(area: AreaSelection): boolean {
  if (area.geometryType === 'slot') {
    return !!area.slotId;
  }
  if (area.geometryType === 'area') {
    return !!area.category && !!area.areaName;
  }
  return !!area.unitType && !!area.unitName;
}

/**
 * Validates that a number is at least a given minimum.
 * @param {number} min - The minimum value
 * @return {function} A validator function
 */
export function validateMin(
  min: number
): (value: number | string) => string | undefined {
  return (value: number | string) => {
    if (value === '') return 'This field is required';
    return Number(value) >= min ? undefined : `Must be at least ${min}`;
  };
}

/**
 * Builds a validator that requires an integer within [min, max] — the client
 * mirror of the server range checks for lags and the LTD start offset.
 * @param {number} min - Inclusive minimum
 * @param {number} max - Inclusive maximum
 * @return {function} A validator function
 */
export function validateRange(
  min: number,
  max: number
): (value: number | string) => string | undefined {
  return (value: number | string) => {
    if (value === '') return 'This field is required';
    const n = Number(value);
    if (!Number.isInteger(n)) return 'Must be a whole number';
    if (n < min || n > max) return `Must be between ${min} and ${max}`;
    return undefined;
  };
}

/**
 * Validates the per-area pace value against the active pace model: [1, 50] under
 * years-to-full-development, any positive number under fixed rate.
 * @param {PaceModel} paceModel - The active pace model
 * @return {function} A validator function
 */
export function validatePaceValue(
  paceModel: PaceModel
): (value: number | string) => string | undefined {
  return (value: number | string) => {
    if (value === '') return 'This field is required';
    const n = Number(value);
    if (Number.isNaN(n)) return 'Must be a number';
    if (paceModel === 'years_to_full_development') {
      return n >= YTFD_MIN && n <= YTFD_MAX
        ? undefined
        : `Must be between ${YTFD_MIN} and ${YTFD_MAX}`;
    }
    return n > 0 ? undefined : 'Must be greater than 0';
  };
}

// ─────────────────────────────────────
// New-area seeding (spec 5.3)
// ─────────────────────────────────────

/**
 * Picks the area a newly-added panel seeds its params from: the most-recently
 * configured area this session when it still exists, otherwise the last area in
 * list order. Returns null for an empty list (first area → pinned defaults).
 * @param {AreaSelection[]} areas - Current areas, in list order
 * @param {number | null} lastConfiguredId - Id of the last area whose params were edited
 * @return {AreaSelection | null} The seed source, or null when there is none
 */
export function pickSeedSource(
  areas: AreaSelection[],
  lastConfiguredId: number | null
): AreaSelection | null {
  if (areas.length === 0) return null;
  if (lastConfiguredId !== null) {
    const touched = areas.find((a) => a.id === lastConfiguredId);
    if (touched) return touched;
  }
  return areas[areas.length - 1];
}

/**
 * Builds a new area by copying the 10 pace params + the Include Active Upside
 * toggle from a source area — a plain value copy with no linkage. Geometry,
 * alias, and notes are deliberately NOT copied (each area targets its own).
 * @param {AreaSelection} source - The area to copy params from
 * @param {number} id - The new area's client id
 * @return {AreaSelection} The seeded new area
 */
export function seedAreaFromSource(
  source: AreaSelection,
  id: number
): AreaSelection {
  return {
    ...DEFAULT_AREA_SELECTION,
    id,
    paceModelValue: source.paceModelValue,
    includeActiveUpside: source.includeActiveUpside,
    wellLifecycle: { ...source.wellLifecycle },
  };
}

// ─────────────────────────────────────
// Type Curve Scenario dropdown (spec 5.5)
// ─────────────────────────────────────

/** A promoted Type Curve Scenario, or the saved-but-revoked selection. */
export interface TypeCurveDropdownItem {
  id: number;
  label: string;
}

/**
 * Builds the Type Curve Scenario dropdown items: the promoted options (name-
 * ordered by the endpoint) plus, when the saved selection is no longer among
 * them, that selection appended as a flagged "(no longer promoted)" option — so
 * a revoked selection is shown, never silently swapped or blanked (5.5).
 * @param {TypeCurveScenarioOption[]} options - Promoted scenarios from the endpoint
 * @param {number | null} selectedId - The currently-saved selection id
 * @param {string | null} selectedName - The saved selection's name (for the flag)
 * @return {TypeCurveDropdownItem[]} The dropdown items in display order
 */
export function buildTypeCurveDropdownItems(
  options: TypeCurveScenarioOption[],
  selectedId: number | null,
  selectedName: string | null
): TypeCurveDropdownItem[] {
  const items: TypeCurveDropdownItem[] = options.map((o) => ({
    id: o.id,
    label: o.name,
  }));

  if (selectedId !== null && !items.some((item) => item.id === selectedId)) {
    items.push({
      id: selectedId,
      label: `${selectedName ?? 'Selected scenario'} (no longer promoted)`,
    });
  }

  return items;
}

// ─────────────────────────────────────
// Save gating (spec 5.4)
// ─────────────────────────────────────

/** The scenario-wide contributors to save gating and their status banners. */
export interface ScenarioStatuses {
  hasName: boolean;
  hasTcScenario: boolean;
  hasCompleteArea: boolean;
  noAreas: boolean;
  hasOverlap: boolean;
  /**
   * The offending ids behind `hasOverlap`. Carried here so the header can name
   * the selected area's overlap without asking for the pairwise scan a second
   * time — the two answers are one computation.
   *
   * Numeric, unlike the shared util's `number | string`: that arm exists only
   * because `OverlapCheckItem` is generic across the scenario forms, and every
   * DPS area id is an `AreaSelection.id`, which is a `number`.
   */
  overlappingAreaIds: ReadonlySet<number>;
}

/**
 * Derives the scenario-level gating statuses (spec 5.4): a save needs a name, a
 * Type Curve Scenario, and at least one geometry-complete area, with no overlap.
 * The per-field inline validity is TanStack's `canSubmit`, combined in the view.
 * @param {DevelopmentPaceScenarioFormData} values - Current form values
 * @param {Record<string, GeoJSON.Geometry>} geometries - Fetched geometries by area id
 * @return {ScenarioStatuses} The derived statuses
 */
export function deriveScenarioStatuses(
  values: DevelopmentPaceScenarioFormData,
  geometries: Record<string, GeoJSON.Geometry>
): ScenarioStatuses {
  const { areas, global } = values;
  // Narrowed, not rebuilt: a fresh `new Set` would throw away the memo's stable
  // reference for no gain. The assertion is sound because `areas` is
  // `AreaSelection[]`, whose ids are numbers — see `ScenarioStatuses`.
  const overlappingAreaIds = getMemoizedOverlappingAreaIds(
    areas,
    geometries
  ) as ReadonlySet<number>;
  return {
    hasName: !!global.configurationName.trim(),
    hasTcScenario: global.typeCurveScenarioId !== null,
    hasCompleteArea: areas.some((a) => isAreaSelectionComplete(a)),
    noAreas: areas.length === 0,
    hasOverlap: overlappingAreaIds.size > 0,
    overlappingAreaIds,
  };
}

const EMPTY_ID_SET: ReadonlySet<number | string> = new Set();

const navItemsMemo = createKeyedMemo<NavItem[]>('buildNavItems');

/**
 * Builds the sidebar's items, returning the previous array when nothing the
 * sidebar shows has changed.
 *
 * Identity is the whole point. The selector this feeds runs on every store
 * emission and every render, and `form.Subscribe` compares its result with
 * `===` on the whole value — so rebuilding the array each pass re-renders every
 * area button in the sidebar on every keystroke, however small the edit.
 * @param {AreaSelection[]} areas - Current areas, in sidebar order
 * @param {ReadonlySet<number | string>} overlappingAreaIds - Areas flagged as overlapping
 * @param {ReadonlySet<number | string>} invalidAreaIds - Areas with an out-of-range parameter
 * @return {NavItem[]} The sidebar items
 */
export function buildNavItems(
  areas: AreaSelection[],
  overlappingAreaIds: ReadonlySet<number | string>,
  invalidAreaIds: ReadonlySet<number | string> = EMPTY_ID_SET
): NavItem[] {
  // Serialized rather than joined on a separator: the display name can be a
  // user-typed alias, so no delimiter is safe to assume absent from it.
  // invalidAreaIds is part of the key because the sidebar renders it (ONCALL-840):
  // omitting it would hand back a stale array when only validity changed.
  const key = JSON.stringify(
    areas.map((area) => [
      area.id,
      getAreaDisplayName(area),
      area.status,
      overlappingAreaIds.has(area.id),
      invalidAreaIds.has(area.id),
    ])
  );

  return navItemsMemo.get(key, () =>
    areas.map((area) => ({
      id: area.id,
      name: getAreaDisplayName(area),
      status: area.status,
      hasOverlap: overlappingAreaIds.has(area.id),
      hasInvalidParams: invalidAreaIds.has(area.id),
    }))
  );
}

/** TEST-ONLY. Drops the memoized sidebar items. */
export function __resetNavItemsMemoForTest(): void {
  navItemsMemo.reset();
}

/**
 * The ids of every area holding an invalid pace value, lag, or LTD offset. The
 * per-field validators only run for the mounted panel, and the pace validator is
 * pace-model-dependent — so a model switch can silently invalidate a value on an
 * unmounted panel. This scans EVERY area against the same rules, keeping save
 * gating correct regardless of which panel is open (spec 5.2 / 5.4).
 *
 * Returns the ids rather than a bare boolean so the view can name the offending
 * areas instead of greying Save with no explanation (ONCALL-840). Touches no
 * geometry — pure per-field arithmetic — so it is safe to call from a selector.
 * @param {DevelopmentPaceScenarioFormData} values - Current form values
 * @return {Set<number>} Ids of areas with an invalid parameter; empty when none
 */
export function findInvalidAreaIds(
  values: DevelopmentPaceScenarioFormData
): Set<number> {
  const { areas, global } = values;
  const paceValidator = validatePaceValue(global.paceModel);
  const lagValidator = validateRange(LAG_MIN, LAG_MAX);
  const offsetValidator = validateRange(LTD_OFFSET_MIN, LTD_OFFSET_MAX);
  const invalid = new Set<number>();

  for (const area of areas) {
    const wl = area.wellLifecycle;
    const isInvalid =
      !!paceValidator(area.paceModelValue) ||
      TIMING_SECTIONS.some(
        ({ prefix }) =>
          !!offsetValidator(wl[timingKey(prefix, 'StartOffsetMonths')])
      ) ||
      [
        ...TIMING_SECTIONS.flatMap(({ prefix }) =>
          LTD_STYLE_LAG_FIELDS.map((field) => wl[timingKey(prefix, field)])
        ),
        wl.permitReferenceToSpudLagMonths,
        wl.permitSpudToCompletionLagMonths,
        wl.permitCompletionToFirstProductionLagMonths,
        wl.ducReferenceToCompletionLagMonths,
        wl.ducCompletionToFirstProductionLagMonths,
      ].some((lag) => !!lagValidator(lag));

    if (isInvalid) invalid.add(area.id);
  }

  return invalid;
}

/**
 * Joins area names for a blocked-save reason, capping the list so one bad
 * scenario with 34 areas cannot produce an unreadable sentence.
 * @param {string[]} names - Area display names, in list order
 * @return {string} A human list, e.g. "Alpha, Bravo and 4 others"
 */
function formatAreaNames(names: string[]): string {
  if (names.length <= 3) {
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  const rest = names.length - 3;
  return `${names.slice(0, 3).join(', ')} and ${rest} ${
    rest === 1 ? 'other' : 'others'
  }`;
}

/** A blocked-save explanation: the reasons to show, and whether any is a real
 *  error as opposed to the transient geometry fetch. */
export interface SaveBlockedExplanation {
  reasons: string[];
  hasHardBlock: boolean;
}

/**
 * The reasons Save is unavailable, in priority order, naming the offending
 * areas (ONCALL-840). Save has six gates and only two of them used to explain
 * themselves — and those two rendered inside the Global panel, so they were
 * invisible from any area panel. This is the single always-visible surface.
 *
 * Pure and geometry-free by construction: it takes the pre-computed id sets
 * rather than the geometries, because it runs inside a form.Subscribe selector
 * on every keystroke and re-deriving overlaps here would add a third full turf
 * pass per render (see deriveScenarioStatuses).
 * @param {object} args - Derived gate inputs
 * @param {DevelopmentPaceScenarioFormData} args.values - Current form values
 * @param {Set<number>} args.invalidAreaIds - From findInvalidAreaIds
 * @param {Set<number>} args.overlappingAreaIds - From getOverlappingAreaIds
 * @param {boolean} args.isGeometryLoading - An area's geometry is still fetching
 * @return {SaveBlockedExplanation} Ordered reasons, plus a hard-block flag
 */
export function buildSaveBlockedReasons({
  values,
  invalidAreaIds,
  overlappingAreaIds,
  isGeometryLoading,
}: {
  values: DevelopmentPaceScenarioFormData;
  invalidAreaIds: ReadonlySet<number>;
  overlappingAreaIds: ReadonlySet<number>;
  isGeometryLoading: boolean;
}): SaveBlockedExplanation {
  const { areas, global } = values;
  const reasons: string[] = [];

  const namesFor = (ids: ReadonlySet<number>) =>
    areas.filter((a) => ids.has(a.id)).map((a) => getAreaDisplayName(a));

  if (!global.configurationName.trim()) {
    reasons.push('Enter a configuration name.');
  }
  if (global.typeCurveScenarioId === null) {
    reasons.push('Select a Type Curve Scenario.');
  }

  const invalidNames = namesFor(invalidAreaIds);
  if (invalidNames.length > 0) {
    reasons.push(`Invalid area parameters: ${formatAreaNames(invalidNames)}.`);
  }

  // Suppressed when there are no areas at all: the standing "No areas"
  // notification already covers that, and this would say nothing useful.
  if (areas.length > 0) {
    const incompleteNames = areas
      .filter((a) => !isAreaSelectionComplete(a))
      .map((a) => getAreaDisplayName(a));
    if (incompleteNames.length > 0) {
      // Areas with no geometry usually have no name either, so they all resolve
      // to the same fallback and listing them names nothing ("New Area, New
      // Area and New Area"). Count them instead; the nav's per-area marker
      // locates them. This is the commonest create path: Add Area a few times.
      const allUnnamed = incompleteNames.every(
        (name) => name === UNNAMED_AREA_LABEL
      );
      reasons.push(
        allUnnamed
          ? `Choose a geometry for ${incompleteNames.length} new ${
              incompleteNames.length === 1 ? 'area' : 'areas'
            }.`
          : `Choose a geometry for: ${formatAreaNames(incompleteNames)}.`
      );
    }
  }

  const overlappingNames = namesFor(overlappingAreaIds);
  if (overlappingNames.length > 0) {
    reasons.push(
      `Overlapping geometries: ${formatAreaNames(overlappingNames)}.`
    );
  }

  const hasHardBlock = reasons.length > 0;

  // Last, and never ahead of a real error: a fetch in flight is transient and
  // resolves itself, so leading with it would hide an actionable problem.
  if (isGeometryLoading) reasons.push('Loading area geometries\u2026');

  return { reasons, hasHardBlock };
}

// ─────────────────────────────────────
// Payload: Form → Backend (snake_case)
// ─────────────────────────────────────

/**
 * Maps WellLifecycleParams to the snake_case backend lifecycle fields: every LTD-style set
 * (LTD's and each Create New Well status's), then Permit's and DUC's.
 * @param {WellLifecycleParams} wl - The well lifecycle params
 * @return {object} Snake_case lifecycle fields
 */
function mapLifecycleToPayload(wl: WellLifecycleParams) {
  return {
    ...Object.fromEntries(
      TIMING_SECTIONS.flatMap(({ prefix }) =>
        LTD_STYLE_TIMING_FIELDS.map((field) => [
          timingBackendKey(prefix, field),
          wl[timingKey(prefix, field)],
        ])
      )
    ),
    permit_reference_to_spud_lag_months: wl.permitReferenceToSpudLagMonths,
    permit_spud_to_completion_lag_months: wl.permitSpudToCompletionLagMonths,
    permit_completion_to_first_production_lag_months:
      wl.permitCompletionToFirstProductionLagMonths,
    duc_reference_to_completion_lag_months:
      wl.ducReferenceToCompletionLagMonths,
    duc_completion_to_first_production_lag_months:
      wl.ducCompletionToFirstProductionLagMonths,
  };
}

/**
 * Maps an AreaSelection to the backend area_config shape (no `id`).
 * @param {AreaSelection} area - The area selection
 * @return {object} Snake_case area config fields
 */
function mapAreaToPayload(area: AreaSelection) {
  return {
    ...mapAreaIdentity(area),
    alias_name: area.alias || null,
    notes: area.notes || null,
    pace_model_value: area.paceModelValue,
    include_active_upside: area.includeActiveUpside,
    ...mapLifecycleToPayload(area.wellLifecycle),
  };
}

/**
 * The area's target identity as the backend's exactly-one trio: a slot area
 * sends target_slot_id with a null geometry pair; a geometry area sends the pair
 * with a null target. Emitted together so switching kinds clears the other side.
 * @param {AreaSelection} area - The area selection.
 * @return {object} { configurable_type, configurable_id, target_slot_id }
 */
function mapAreaIdentity(area: AreaSelection) {
  if (area.geometryType === 'slot') {
    return {
      configurable_type: null,
      configurable_id: null,
      target_slot_id: area.slotId || null,
    };
  }
  return {
    configurable_type:
      area.geometryType === 'area' ? 'AreaGeometry' : 'DevelopmentArea',
    configurable_id: area.configurableId,
    target_slot_id: null,
  };
}

/**
 * Maps a form AreaSelection's geometry to the capture seam's geometry union.
 * Returns null for an incomplete area (no configurable selected), which the
 * capture endpoint skips in place (zeroed positional summary).
 * @param {AreaSelection} area - The area selection.
 * @return {AreaGeometrySelection | null} The geometry, or null when incomplete.
 */
function mapAreaGeometry(area: AreaSelection): AreaGeometrySelection | null {
  if (area.geometryType === 'slot') {
    return area.slotId ? { geometryType: 'slot', slotId: area.slotId } : null;
  }
  if (area.configurableId === null) return null;
  if (area.geometryType === 'unit') {
    return { geometryType: 'unit', developmentAreaId: area.configurableId };
  }
  return {
    geometryType: 'area',
    areaCategory: area.category as Extract<
      AreaGeometrySelection,
      { geometryType: 'area' }
    >['areaCategory'],
    areaGeometryId: area.configurableId,
  };
}

/**
 * Maps one form area to the capture seam's AreaConfigDraft.
 * @param {AreaSelection} area - The area selection.
 * @return {AreaConfigDraft} The capture-side draft for that area.
 */
function mapAreaToDraft(area: AreaSelection): AreaConfigDraft {
  const wl = area.wellLifecycle;
  return {
    alias: area.alias || undefined,
    geometry: mapAreaGeometry(area),
    includeActiveUpside: area.includeActiveUpside,
    params: {
      paceModelValue: area.paceModelValue,
      ltdStartOffsetMonths: wl.ltdStartOffsetMonths,
      ltdPermitToSpudLagMonths: wl.ltdPermitToSpudLagMonths,
      ltdSpudToCompletionLagMonths: wl.ltdSpudToCompletionLagMonths,
      ltdCompletionToFirstProductionLagMonths:
        wl.ltdCompletionToFirstProductionLagMonths,
      ...pickPrePermitTiming(wl),
      permitReferenceToSpudLagMonths: wl.permitReferenceToSpudLagMonths,
      permitSpudToCompletionLagMonths: wl.permitSpudToCompletionLagMonths,
      permitCompletionToFirstProductionLagMonths:
        wl.permitCompletionToFirstProductionLagMonths,
      ducReferenceToCompletionLagMonths: wl.ducReferenceToCompletionLagMonths,
      ducCompletionToFirstProductionLagMonths:
        wl.ducCompletionToFirstProductionLagMonths,
    },
  };
}

/**
 * Builds the ephemeral capture definition from the current form state, or null
 * when no capture should run — no Type Curve Scenario selected, or no area yet
 * carries a geometry (the recompute is gated on both, AC 1/2). The returned
 * object is the debounce key: any edit that changes it triggers a recompute.
 *
 * @param {DevelopmentPaceScenarioFormData} values - The current form values.
 * @return {EphemeralScenarioDefinition | null} The definition, or null.
 */
export function buildCaptureDefinition(
  values: DevelopmentPaceScenarioFormData
): EphemeralScenarioDefinition | null {
  const { global: g } = values;
  if (g.typeCurveScenarioId === null) return null;

  const areaConfigs = values.areas.map(mapAreaToDraft);
  if (!areaConfigs.some((area) => area.geometry !== null)) return null;

  return {
    typeCurveScenarioId: g.typeCurveScenarioId,
    referenceDateMode: g.referenceDateMode,
    referenceDate: g.referenceDate || undefined,
    paceModel: g.paceModel,
    slotPrioritizationMethod: g.slotPrioritizationMethod,
    geometryPriority: g.geometryPriority,
    areaConfigs,
  };
}

/**
 * Builds the full payload for creating a new scenario (POST).
 * @param {DevelopmentPaceScenarioFormData} values - The form values
 * @return {object} The wrapped payload
 */
export function buildCreatePayload(values: DevelopmentPaceScenarioFormData) {
  const { global: g } = values;

  return {
    development_pace_scenario: {
      name: g.configurationName,
      pace_model: g.paceModel,
      reference_date: g.referenceDate,
      reference_date_mode: g.referenceDateMode,
      geometry_priority: g.geometryPriority,
      slot_prioritization_method: g.slotPrioritizationMethod,
      type_curve_scenario_id: g.typeCurveScenarioId,
      notes: g.notes || null,
      area_configs_attributes: values.areas.map(mapAreaToPayload),
    },
  };
}

/** Maps a form global field path to its snake_case backend key. */
const GLOBAL_FIELD_MAP: Record<string, string> = {
  'global.configurationName': 'name',
  'global.paceModel': 'pace_model',
  'global.referenceDate': 'reference_date',
  'global.referenceDateMode': 'reference_date_mode',
  'global.geometryPriority': 'geometry_priority',
  'global.slotPrioritizationMethod': 'slot_prioritization_method',
  'global.typeCurveScenarioId': 'type_curve_scenario_id',
  'global.notes': 'notes',
};

/**
 * Area field suffixes that make up the target identity. When any is dirty the
 * whole exactly-one trio is re-emitted together (mapAreaIdentity), so a
 * kind-switch never sends a half-updated identity the CHECK would reject.
 */
const AREA_IDENTITY_KEYS = ['geometryType', 'configurableId', 'slotId'];

/** Maps an area field suffix to its snake_case backend key. */
const AREA_FIELD_MAP: Record<string, string> = {
  'alias': 'alias_name',
  'notes': 'notes',
  'paceModelValue': 'pace_model_value',
  'wellLifecycle.ltdStartOffsetMonths': 'ltd_start_offset_months',
  'wellLifecycle.ltdPermitToSpudLagMonths': 'ltd_permit_to_spud_lag_months',
  'wellLifecycle.ltdSpudToCompletionLagMonths':
    'ltd_spud_to_completion_lag_months',
  'wellLifecycle.ltdCompletionToFirstProductionLagMonths':
    'ltd_completion_to_first_production_lag_months',
  // Each Create New Well status's own timing (timing-sections).
  ...Object.fromEntries(
    PRE_PERMIT_TIMING_SECTIONS.flatMap(({ prefix }) =>
      LTD_STYLE_TIMING_FIELDS.map((field) => [
        `wellLifecycle.${timingKey(prefix, field)}`,
        timingBackendKey(prefix, field),
      ])
    )
  ),
  'wellLifecycle.permitReferenceToSpudLagMonths':
    'permit_reference_to_spud_lag_months',
  'wellLifecycle.permitSpudToCompletionLagMonths':
    'permit_spud_to_completion_lag_months',
  'wellLifecycle.permitCompletionToFirstProductionLagMonths':
    'permit_completion_to_first_production_lag_months',
  'wellLifecycle.ducReferenceToCompletionLagMonths':
    'duc_reference_to_completion_lag_months',
  'wellLifecycle.ducCompletionToFirstProductionLagMonths':
    'duc_completion_to_first_production_lag_months',
  'includeActiveUpside': 'include_active_upside',
};

/**
 * Builds a diff payload for updating an existing scenario (PATCH).
 * Uses fieldMeta.isDirty from TanStack form for first-pass filtering.
 * @param {DevelopmentPaceScenarioFormData} current - Current form values
 * @param {number[]} removedAreaIds - Backend IDs of deleted areas
 * @param {Record} fieldMeta - TanStack form field metadata
 * @return {object} The wrapped diff payload, or null if nothing changed
 */
export function buildUpdatePayload(
  current: DevelopmentPaceScenarioFormData,
  removedAreaIds: number[],
  fieldMeta: Partial<Record<string, { isDirty?: boolean }>>
): { development_pace_scenario: Record<string, unknown> } | null {
  const dirtyKeys = new Set(
    Object.keys(fieldMeta).filter((k) => fieldMeta[k]?.isDirty)
  );

  const scenario: Record<string, unknown> = {};

  // Global field diff
  for (const [formPath, backendKey] of Object.entries(GLOBAL_FIELD_MAP)) {
    if (!dirtyKeys.has(formPath)) continue;

    scenario[backendKey] = get(current, formPath);
  }

  // Area configs diff
  const areaConfigsAttrs: Record<string, unknown>[] = [];

  // Destroyed areas
  for (const id of removedAreaIds) {
    areaConfigsAttrs.push({ id, _destroy: true });
  }

  // New areas
  for (const area of current.areas) {
    if (area.status === 'new') {
      areaConfigsAttrs.push(mapAreaToPayload(area));
    }
  }

  // Modified saved areas
  for (let i = 0; i < current.areas.length; i++) {
    const area = current.areas[i];
    if (area.status !== 'saved') continue;

    const prefix = `areas[${i}]`;
    const areaDirtyKeys = [...dirtyKeys].filter((k) => k.startsWith(prefix));
    if (areaDirtyKeys.length === 0) continue;

    const changedFields: Record<string, unknown> = { id: area.id };
    const dirtySuffixes = areaDirtyKeys.map((k) => k.replace(`${prefix}.`, ''));

    // Any identity change re-emits the full exactly-one trio together.
    if (dirtySuffixes.some((suffix) => AREA_IDENTITY_KEYS.includes(suffix))) {
      Object.assign(changedFields, mapAreaIdentity(area));
    }

    for (const suffix of dirtySuffixes) {
      const backendKey = AREA_FIELD_MAP[suffix];
      if (!backendKey) continue;

      changedFields[backendKey] = get(area, suffix);
    }

    if (Object.keys(changedFields).length > 1) {
      areaConfigsAttrs.push(changedFields);
    }
  }

  if (areaConfigsAttrs.length > 0) {
    scenario.area_configs_attributes = areaConfigsAttrs;
  }

  if (Object.keys(scenario).length === 0) return null;

  return { development_pace_scenario: scenario };
}

// ─────────────────────────────────────
// Response mapper: Backend → Form (camelCase)
// ─────────────────────────────────────

/**
 * Each Create New Well status's timing from a saved area: its own value when the response
 * carries one, else the area's LTD value.
 * @param {AreaConfigResponse} ac - The area config from the API response
 * @param {LtdTimingParams} ltd - That area's LTD timing
 * @return {PrePermitTimingParams} The four statuses' fields
 */
function prePermitTimingWithLtdFallback(
  ac: AreaConfigResponse,
  ltd: LtdTimingParams
): PrePermitTimingParams {
  const fallback = prePermitTimingFromLtd(ltd);
  return Object.fromEntries(
    Object.entries(fallback).map(([key, value]) => [
      key,
      ac[key as keyof PrePermitTimingParams] ?? value,
    ])
  ) as PrePermitTimingParams;
}

/**
 * Maps a backend area config response to the form's AreaSelection shape.
 * @param {AreaConfigResponse} ac - The area config from API response
 * @return {AreaSelection} The form area selection
 */
function mapAreaConfigToSelection(ac: AreaConfigResponse): AreaSelection {
  const ltd = {
    ltdStartOffsetMonths: ac.ltdStartOffsetMonths ?? 0,
    ltdPermitToSpudLagMonths: ac.ltdPermitToSpudLagMonths ?? 0,
    ltdSpudToCompletionLagMonths: ac.ltdSpudToCompletionLagMonths ?? 0,
    ltdCompletionToFirstProductionLagMonths:
      ac.ltdCompletionToFirstProductionLagMonths ?? 0,
  };
  const isSlot = ac.targetSlotId !== null;
  const isArea = ac.configurableType === 'AreaGeometry';
  return {
    id: ac.id,
    status: 'saved',
    geometryType: isSlot ? 'slot' : isArea ? 'area' : 'unit',
    configurableId: ac.configurableId,
    category: ac.configurableCategory ?? '',
    areaName: !isSlot && isArea ? (ac.configurableDisplayName ?? '') : '',
    unitType: ac.configurableUnitType ?? '',
    unitName: !isSlot && !isArea ? (ac.configurableDisplayName ?? '') : '',
    // The slot identity round-trips exactly; the label is the folded display name.
    slotId: ac.targetSlotId ?? '',
    slotLabel: isSlot ? (ac.configurableDisplayName ?? '') : '',
    alias: ac.aliasName ?? '',
    notes: ac.notes ?? '',
    paceModelValue: Number(ac.paceModelValue) || 5,
    includeActiveUpside: ac.includeActiveUpside ?? false,
    wellLifecycle: {
      ...ltd,
      // A status's own value, else the area's own LTD value (as the server backfill did).
      ...prePermitTimingWithLtdFallback(ac, ltd),
      permitReferenceToSpudLagMonths: ac.permitReferenceToSpudLagMonths ?? 0,
      permitSpudToCompletionLagMonths: ac.permitSpudToCompletionLagMonths ?? 0,
      permitCompletionToFirstProductionLagMonths:
        ac.permitCompletionToFirstProductionLagMonths ?? 0,
      ducReferenceToCompletionLagMonths:
        ac.ducReferenceToCompletionLagMonths ?? 0,
      ducCompletionToFirstProductionLagMonths:
        ac.ducCompletionToFirstProductionLagMonths ?? 0,
    },
  };
}

/**
 * Converts a backend scenario response into form data and geometries.
 * @param {DevelopmentPaceScenarioResponse} res - The API response
 * @return {{ formData: DevelopmentPaceScenarioFormData, geometries: Record<number, GeoJSON.Geometry> }}
 */
export function mapResponseToFormData(res: DevelopmentPaceScenarioResponse): {
  formData: DevelopmentPaceScenarioFormData;
  geometries: Record<number, GeoJSON.Geometry>;
} {
  const geometries: Record<number, GeoJSON.Geometry> = {};

  const areas = res.areaConfigs.map((ac) => {
    const selection = mapAreaConfigToSelection(ac);
    if (ac.configurableGeojson) {
      const geojson = ac.configurableGeojson;
      // Response may be a Feature (with .geometry) or a bare Geometry
      geometries[ac.id] =
        'geometry' in geojson && geojson.geometry
          ? geojson.geometry
          : (geojson as unknown as GeoJSON.Geometry);
    }
    return selection;
  });

  return {
    formData: {
      global: {
        configurationName: res.name,
        notes: res.notes ?? '',
        paceModel: res.paceModel,
        referenceDateMode: res.referenceDateMode ?? 'fixed',
        // Effective, not stored: while a scenario is on current_month the stored
        // date is a re-stamping artifact from its last save, so hydrating the
        // editable field from it would show a stale month and make switching to
        // Fixed look like it resurrected one (INS-10499).
        referenceDate: moment(res.effectiveReferenceDate ?? res.referenceDate)
          .startOf('month')
          .format('YYYY-MM-DD'),
        slotPrioritizationMethod: res.slotPrioritizationMethod,
        geometryPriority: res.geometryPriority,
        typeCurveScenarioId: res.typeCurveScenarioId,
        typeCurveScenarioName: res.typeCurveScenarioName ?? null,
      },
      areas,
    },
    geometries,
  };
}
